import { useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { v4 as uuid } from "uuid";
import { useSetRecoilState } from "recoil";
import { authState, fetchState } from "../recoil";
import { defaultAuthState } from "../recoil/authState";

// Common api
const api = axios.create();
// Set baseURL
api.defaults.baseURL = process.env.REACT_APP_API_PTLI;
// Set paramsSerializer
api.defaults.paramsSerializer = (params) => {
    const nextParams = new URLSearchParams();
    for (const key in params) {
        const value = params[key];
        if (value !== undefined) {
            if (Array.isArray(value)) {
                value.forEach((item) => {
                    nextParams.append(key, item);
                });
            } else if (value === null) {
                nextParams.append(key, "");
            } else {
                nextParams.append(key, value);
            }
        }
    }
    return nextParams.toString();
};
// Set headers
api.interceptors.request.use(
    (config) => {
        const lang = _TEST1234.LANGUAGE;
        const authorization = _TEST1234.AUTHORIZATION;
        const refreshauthorization = _TEST1234.REFRESH_AUTHORIZATION;
        config.headers["Accept-Language"] = lang;
        if (authorization) config.headers["Authorization"] = "Bearer " + authorization;
        if (refreshauthorization) config.headers["Refreshauthorization"] = "Bearer " + refreshauthorization;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);
// Renewal authorization
api.interceptors.response.use(
    (response) => {
        const { authorization } = response.headers;
        if (authorization) {
            const token = authorization.startsWith("Bearer ") ? authorization.substr(7) : authorization;
            _TEST1234.AUTHORIZATION = token;
        }
        return response;
    },
    (error) => {
        return Promise.reject(error);
    },
);

const ApiConfig = () => {
    const setAuth = useSetRecoilState(authState);
    const setFetch = useSetRecoilState(fetchState);

    const $ = useRef({ initialized: false });

    if ($.current.initialized === false) {
        $.current.initialized = true;

        const done = (_uuid: any) => {
            if (!_uuid) return;
            setFetch((prev) => {
                const requests = { ...prev.requests };
                delete requests[_uuid];
                return { ...prev, requests };
            });
        };

        api.interceptors.request.use(
            (config) => {
                if (config.headers._disableIndicator) {
                    delete config.headers._disableIndicator;
                } else {
                    const _uuid = uuid();
                    //@ts-ignore
                    config._uuid = _uuid;
                    setFetch((prev) => {
                        const requests = { ...prev.requests, [_uuid]: true };
                        return { ...prev, requests };
                    });
                }
                return config;
            },
            (error) => {
                done(error.config._uuid);
                return Promise.reject(error);
            },
        );

        api.interceptors.response.use(
            (response) => {
                //@ts-ignore
                done(response.config._uuid);
                return response;
            },
            (error) => {
                done(error.config._uuid);
                return Promise.reject(error);
            },
        );

        /** Response interceptor */
        axios.interceptors.response.use(
            (response) => {
                switch (response.status) {
                    case 202: {
                        const movePage = response.headers["movepage"];
                        if (movePage) {
                            const destination = window.location.origin + movePage;
                            if (window.location.href !== destination) {
                                window.location.href = destination;
                            }
                        }
                        break;
                    }
                }
                return response;
            },
            (error) => {
                if (error.response) {
                    const movePage = error.response.headers["movepage"];
                    if (movePage) {
                        const destination = window.location.origin + movePage;
                        if (window.location.href !== destination) {
                            window.location.href = destination;
                        }
                    }

                    switch (error.response.status) {
                        // case 301: {
                        //     const movePage = error.response.headers["movepage"];
                        //     if (movePage) {
                        //         const destination = window.location.origin + movePage;
                        //         if (window.location.href !== destination) {
                        //             window.location.href = destination;
                        //         }
                        //     }
                        //     break;
                        // }
                        case 401: {
                            if (_TEST1234.AUTHORIZATION) {
                                Cookies.remove("authorization");
                                Cookies.remove("refreshauthorization");
                                Cookies.remove(`authorization_${_TEST1234.SYSTEM_GROUP_ORI}`);
                                Cookies.remove(`refreshauthorization_${_TEST1234.SYSTEM_GROUP_ORI}`);
                                if (window.name.length === 41 && window.name.indexOf("-link") === 36) {
                                    const authlinkKey = window.name.slice(0, -5);
                                    const domain =
                                        window.location.hostname.split(".").length > 1
                                            ? window.location.hostname.replace(
                                                  window.location.hostname.split(".")[0],
                                                  "",
                                              )
                                            : window.location.hostname;
                                    Cookies.remove(`${authlinkKey}_a`, { domain });
                                    Cookies.remove(`${authlinkKey}_r`, { domain });
                                }

                                window.sessionStorage.removeItem("user-info");
                                setAuth({
                                    ...defaultAuthState,
                                    isInitialized: true,
                                });
                                switch (_TEST1234.SYSTEM_GROUP_ORI) {
                                    case "tancis":
                                    case "tanoga":
                                        window.location.href = window.location.origin + "/login";
                                        break;
                                    case "tanesw":
                                        window.location.href = window.location.origin + "/ptle";
                                        break;
                                }
                            }

                            break;
                        }
                    }
                }

                return Promise.reject(error);
            },
        );
    }

    return null;
};

/* 임시 공통 개발용 */
const _TEST1234 = {
    NODE_ENV: process.env.NODE_ENV,
    LANGUAGES: ["en", "tz", "ko"],
    SYSTEM_GROUPS: ["tancis", "tanesw", "tanoga"],
    get LANGUAGE() {
        const lang = this.LANGUAGES.find((value) => value === Cookies.get("lang")) || "en";
        return lang.toUpperCase();
    },
    get SYSTEM_GROUP_ENV() {
        return this.SYSTEM_GROUPS.find((value) => value === process.env.REACT_APP_SYSTEM_GROUP) || "";
    },
    get SYSTEM_GROUP_ORI() {
        switch (this.NODE_ENV) {
            case "production": {
                const subdomain = window.location.hostname.substring(0, window.location.hostname.indexOf("."));
                let group;
                if (subdomain.indexOf("tanesw") > -1) {
                    group = "tanesw";
                } else if (subdomain.indexOf("tanoga") > -1) {
                    group = "tanoga";
                } else if (subdomain.indexOf("tancis") > -1) {
                    group = "tancis";
                }
                if (group) return group;
                else break;
            }
            default:
                return this.SYSTEM_GROUP_ENV;
        }
    },
    get PORTAL_API_ENV() {
        switch (this.SYSTEM_GROUP_ENV) {
            case "tancis":
                return process.env.REACT_APP_API_PTLI || "";
            default: // tanesw, tanoga
                return process.env.REACT_APP_API_PTLE || "";
        }
    },
    get PORTAL_API_ORI() {
        switch (this.SYSTEM_GROUP_ORI) {
            case "tancis":
                return process.env.REACT_APP_API_PTLI || "";
            default: // tanesw, tanoga
                return process.env.REACT_APP_API_PTLE || "";
        }
    },
    get SYSTEM() {
        switch (this.SYSTEM_GROUP_ORI) {
            case "tancis":
                return "internal";
            default: // tanesw, tanoga
                return "external";
        }
    },
    get AUTHORIZATION() {
        switch (this.NODE_ENV) {
            case "production": {
                if (window.name.length === 41 && window.name.indexOf("-link") === 36) {
                    const authlinkKey = window.name.slice(0, -5);
                    return Cookies.get(`${authlinkKey}_a`) || "";
                }
                return Cookies.get("authorization") || "";
            }
            default: {
                if (Cookies.get("oga") === "true") {
                    return Cookies.get(`authorization_tanoga`) || "";
                }
                return Cookies.get(`authorization_${this.SYSTEM_GROUP_ORI}`) || "";
            }
        }
    },
    get REFRESH_AUTHORIZATION() {
        switch (this.NODE_ENV) {
            case "production": {
                if (window.name.length === 41 && window.name.indexOf("-link") === 36) {
                    const authlinkKey = window.name.slice(0, -5);
                    return Cookies.get(`${authlinkKey}_r`) || "";
                }
                return Cookies.get("refreshauthorization") || "";
            }
            default: {
                if (Cookies.get("oga") === "true") {
                    return Cookies.get(`refreshauthorization_tanoga`) || "";
                }
                return Cookies.get(`refreshauthorization_${this.SYSTEM_GROUP_ORI}`) || "";
            }
        }
    },
    set AUTHORIZATION(value) {
        Cookies.set("authorization", value, { expires: 30 });
        if (this.NODE_ENV !== "production") {
            Cookies.set(`authorization_${this.SYSTEM_GROUP_ORI}`, value, { expires: 30 });
        }
    },
    set REFRESH_AUTHORIZATION(value) {
        Cookies.set("refreshauthorization", value, { expires: 30 });
        if (this.NODE_ENV !== "production") {
            Cookies.set(`refreshauthorization_${this.SYSTEM_GROUP_ORI}`, value, { expires: 30 });
        }
    },
};

export { api, _TEST1234 };

export default ApiConfig;
